import { FormEvent, useEffect, useId, useRef, useState } from 'react';
import {
  CheckCircle2,
  FileText,
  GripVertical,
  KeyRound,
  RotateCcw,
  Save,
  ShieldCheck,
  Trash2,
  Upload,
  UserRound,
  X,
  ZoomIn,
} from 'lucide-react';
import { api, type User, uploadImage } from '../../api';
import './profile.css';

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const AVATAR_OUTPUT_SIZE = 512;

type AvatarCrop = { zoom: number; x: number; y: number };
const CENTERED_CROP: AvatarCrop = { zoom: 1, x: 0, y: 0 };

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

function avatarCropRect(image: HTMLImageElement, crop: AvatarCrop) {
  const zoom = clamp(crop.zoom, 1, 3);
  const size = Math.min(image.naturalWidth, image.naturalHeight) / zoom;
  return {
    x: (image.naturalWidth - size) * ((clamp(crop.x, -1, 1) + 1) / 2),
    y: (image.naturalHeight - size) * ((clamp(crop.y, -1, 1) + 1) / 2),
    size,
  };
}

function drawAvatarCrop(canvas: HTMLCanvasElement, image: HTMLImageElement, crop: AvatarCrop, outputSize: number) {
  const context = canvas.getContext('2d');
  if (!context || !image.naturalWidth || !image.naturalHeight) return false;
  const source = avatarCropRect(image, crop);
  canvas.width = outputSize;
  canvas.height = outputSize;
  context.clearRect(0, 0, outputSize, outputSize);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, source.x, source.y, source.size, source.size, 0, 0, outputSize, outputSize);
  return true;
}

async function croppedAvatarFile(image: HTMLImageElement, source: File, crop: AvatarCrop) {
  const canvas = document.createElement('canvas');
  if (!drawAvatarCrop(canvas, image, crop, AVATAR_OUTPUT_SIZE)) throw new Error('Не удалось подготовить фото');
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(result => result ? resolve(result) : reject(new Error('Не удалось обрезать фото')), source.type, .9);
  });
  const extension = source.type === 'image/png' ? 'png' : source.type === 'image/webp' ? 'webp' : 'jpg';
  return new File([blob], `avatar.${extension}`, { type: source.type, lastModified: Date.now() });
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0]?.slice(0, 2) || 'П')
    .toLocaleUpperCase('ru-RU');
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Не удалось сохранить изменения';
}

export function UserAvatar({ user, className = '' }: { user: User; className?: string }) {
  const [imageFailed, setImageFailed] = useState(false);
  const version = user.avatarUpdatedAt || '';

  useEffect(() => setImageFailed(false), [user.id, version]);

  return <span className={`profile-avatar ${className}`.trim()} aria-label={`Профиль: ${user.fullName}`}>
    {version && !imageFailed
      ? <img
          src={`/api/v1/auth/profile/avatar?v=${encodeURIComponent(version)}`}
          alt={`Фото ${user.fullName}`}
          onError={() => setImageFailed(true)}
        />
      : <span aria-hidden="true">{initials(user.fullName)}</span>}
  </span>;
}

type ProfileDialogProps = {
  user: User;
  open: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

export function ProfileDialog({ user, open, onClose, onSaved }: ProfileDialogProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLElement | null>(null);
  const cropImageRef = useRef<HTMLImageElement | null>(null);
  const cropCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const cropDragRef = useRef<{ pointerId: number; x: number; y: number; cropX: number; cropY: number } | null>(null);
  const pendingRef = useRef<'name' | 'photo' | 'remove-photo' | 'password' | ''>('');
  const [fullName, setFullName] = useState(user.fullName);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoReady, setPhotoReady] = useState(false);
  const [crop, setCrop] = useState<AvatarCrop>(CENTERED_CROP);
  const [photoError, setPhotoError] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState<'name' | 'photo' | 'remove-photo' | 'password' | ''>('');
  pendingRef.current = pending;

  useEffect(() => {
    if (!open) return;
    setFullName(user.fullName);
    setPhoto(null);
    setPhotoReady(false);
    setCrop(CENTERED_CROP);
    setPhotoError('');
    setProfileError('');
    setPasswordError('');
    setNotice('');
  }, [open, user.fullName]);

  useEffect(() => {
    if (!photo || typeof URL.createObjectURL !== 'function') {
      setPhotoPreview('');
      return;
    }
    const preview = URL.createObjectURL(photo);
    setPhotoPreview(preview);
    return () => URL.revokeObjectURL(preview);
  }, [photo]);

  useEffect(() => {
    const image = cropImageRef.current;
    const canvas = cropCanvasRef.current;
    if (!photoReady || !image || !canvas) return;
    drawAvatarCrop(canvas, image, crop, 320);
  }, [photoReady, crop, photoPreview]);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusable = () => [...(dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])',
    ) || [])].filter(element => !element.hasAttribute('hidden'));
    window.setTimeout(() => focusable()[0]?.focus(), 0);

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (pendingRef.current) return;
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      opener?.focus();
    };
  }, [open]);

  if (!open) return null;

  const passwordAvailable = user.authSource !== 'ACTIVE_DIRECTORY';
  const sourceLabel = user.authSource === 'ACTIVE_DIRECTORY'
    ? 'Active Directory'
    : user.authSource === 'HYBRID'
      ? 'Локально + Active Directory'
      : 'Локальная учётная запись';

  async function saveName(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextName = fullName.trim();
    if (nextName.length < 2) {
      setProfileError('Укажите имя длиной не менее 2 символов');
      return;
    }
    setPending('name');
    setProfileError('');
    setNotice('');
    try {
      await api('/auth/profile', { method: 'PATCH', body: JSON.stringify({ fullName: nextName }) });
      await onSaved();
      setNotice('Имя обновлено');
    } catch (error) {
      setProfileError(errorMessage(error));
    } finally {
      setPending('');
    }
  }

  function choosePhoto(file: File | undefined) {
    setPhoto(null);
    setPhotoReady(false);
    setCrop(CENTERED_CROP);
    setPhotoError('');
    setNotice('');
    if (!file) return;
    if (!PHOTO_TYPES.has(file.type)) {
      setPhotoError('Выберите фото в формате JPEG, PNG или WebP');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoError('Размер фото должен быть не больше 5 МБ');
      return;
    }
    setPhoto(file);
  }

  async function savePhoto() {
    const image = cropImageRef.current;
    if (!photo || !photoReady || !image) return;
    setPending('photo');
    setPhotoError('');
    setNotice('');
    try {
      const croppedPhoto = await croppedAvatarFile(image, photo, crop);
      await uploadImage('/auth/profile/avatar', croppedPhoto);
      await onSaved();
      setPhoto(null);
      setPhotoReady(false);
      setCrop(CENTERED_CROP);
      setNotice('Фото профиля обновлено');
    } catch (error) {
      setPhotoError(errorMessage(error));
    } finally {
      setPending('');
    }
  }

  function cancelPhotoSelection() {
    setPhoto(null);
    setPhotoReady(false);
    setCrop(CENTERED_CROP);
    setPhotoError('');
  }

  function moveCrop(deltaX: number, deltaY: number) {
    setCrop(current => ({
      ...current,
      x: clamp(current.x + deltaX, -1, 1),
      y: clamp(current.y + deltaY, -1, 1),
    }));
  }

  function startCropDrag(event: React.PointerEvent<HTMLCanvasElement>) {
    cropDragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, cropX: crop.x, cropY: crop.y };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function continueCropDrag(event: React.PointerEvent<HTMLCanvasElement>) {
    const drag = cropDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const width = Math.max(1, event.currentTarget.getBoundingClientRect().width);
    setCrop(current => ({
      ...current,
      x: clamp(drag.cropX - ((event.clientX - drag.x) / width) * 2, -1, 1),
      y: clamp(drag.cropY - ((event.clientY - drag.y) / width) * 2, -1, 1),
    }));
  }

  function stopCropDrag(event: React.PointerEvent<HTMLCanvasElement>) {
    if (cropDragRef.current?.pointerId !== event.pointerId) return;
    cropDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }

  function handleCropKey(event: React.KeyboardEvent<HTMLCanvasElement>) {
    const step = event.shiftKey ? .2 : .06;
    if (event.key === 'ArrowLeft') moveCrop(-step, 0);
    else if (event.key === 'ArrowRight') moveCrop(step, 0);
    else if (event.key === 'ArrowUp') moveCrop(0, -step);
    else if (event.key === 'ArrowDown') moveCrop(0, step);
    else return;
    event.preventDefault();
  }

  async function removePhoto() {
    setPending('remove-photo');
    setPhotoError('');
    setNotice('');
    try {
      await api('/auth/profile/avatar', { method: 'DELETE' });
      await onSaved();
      setPhoto(null);
      setNotice('Фото профиля удалено');
    } catch (error) {
      setPhotoError(errorMessage(error));
    } finally {
      setPending('');
    }
  }

  async function savePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const currentPassword = String(form.get('currentPassword') || '');
    const newPassword = String(form.get('newPassword') || '');
    const confirmation = String(form.get('passwordConfirmation') || '');
    setPasswordError('');
    setNotice('');
    if (newPassword.length < 12) {
      setPasswordError('Новый пароль должен содержать не менее 12 символов');
      return;
    }
    if (newPassword !== confirmation) {
      setPasswordError('Новые пароли не совпадают');
      return;
    }
    setPending('password');
    try {
      await api('/auth/password/change', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      formElement.reset();
      setNotice('Пароль изменён');
    } catch (error) {
      setPasswordError(errorMessage(error));
    } finally {
      setPending('');
    }
  }

  return <div
    className="profile-dialog-backdrop"
    role="presentation"
    onMouseDown={event => { if (event.currentTarget === event.target && !pending) onClose(); }}
  >
    <section ref={dialogRef} className="profile-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <header className="profile-dialog-header">
        <span className="profile-dialog-mark"><UserRound /></span>
        <div>
          <small>ЛИЧНЫЙ ПРОФИЛЬ</small>
          <h2 id={titleId}>Настройки аккаунта</h2>
          <p>Фото, отображаемое имя и безопасность учётной записи.</p>
        </div>
        <button type="button" onClick={onClose} disabled={Boolean(pending)} aria-label="Закрыть профиль"><X /></button>
      </header>

      <div className="profile-dialog-body">
        <aside className="profile-identity-card">
          {photoPreview ? <div className="profile-crop-editor">
            <div className="profile-crop-heading">
              <span><GripVertical /></span>
              <div><b>Настройте кадр</b><small>Перетащите фото и выберите масштаб</small></div>
            </div>
            <div className={`profile-crop-stage${photoReady ? ' is-ready' : ''}`}>
              <img
                ref={cropImageRef}
                className="profile-crop-source"
                src={photoPreview}
                alt=""
                hidden
                onLoad={() => setPhotoReady(true)}
                onError={() => { setPhotoReady(false); setPhotoError('Не удалось открыть выбранное фото'); }}
              />
              <canvas
                ref={cropCanvasRef}
                width={320}
                height={320}
                role="img"
                aria-label="Кадр будущего фото профиля. Перетаскивайте мышью или используйте стрелки."
                tabIndex={0}
                onPointerDown={startCropDrag}
                onPointerMove={continueCropDrag}
                onPointerUp={stopCropDrag}
                onPointerCancel={stopCropDrag}
                onKeyDown={handleCropKey}
              />
              {!photoReady && <span className="profile-crop-loading">Подготавливаем фото…</span>}
              <span className="profile-crop-ring" aria-hidden="true" />
            </div>
            <div className="profile-crop-controls">
              <label>
                <span><ZoomIn /> Масштаб</span>
                <input
                  name="avatarZoom"
                  type="range"
                  min="1"
                  max="3"
                  step="0.05"
                  value={crop.zoom}
                  onChange={event => setCrop(current => ({ ...current, zoom: Number(event.target.value) }))}
                />
              </label>
              <button type="button" onClick={() => setCrop(CENTERED_CROP)}><RotateCcw /> По центру</button>
            </div>
          </div> : <UserAvatar user={user} className="profile-avatar-large" />}
          <div className="profile-identity-copy">
            <h3>{user.fullName}</h3>
            <p>{user.email}</p>
            <span><ShieldCheck /> {sourceLabel}</span>
          </div>

          <div className="profile-photo-actions">
            <label className="profile-photo-picker">
              <Upload /> Выбрать фото
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={event => choosePhoto(event.target.files?.[0])}
              />
            </label>
            {photo && <div className="profile-selected-photo"><FileText /><span><b>{photo.name}</b><small>{Math.max(1, Math.round(photo.size / 1024))} КБ</small></span></div>}
            <button type="button" className="profile-button primary" onClick={savePhoto} disabled={!photo || !photoReady || Boolean(pending)}>
              <Save /> {pending === 'photo' ? 'Загружаем…' : 'Сохранить фото'}
            </button>
            {photo && <button type="button" className="profile-button secondary" onClick={cancelPhotoSelection} disabled={Boolean(pending)}>
              <X /> Отменить выбор
            </button>}
            {user.avatarUpdatedAt && <button type="button" className="profile-button danger" onClick={removePhoto} disabled={Boolean(pending)}>
              <Trash2 /> {pending === 'remove-photo' ? 'Удаляем…' : 'Удалить фото'}
            </button>}
            <small className="profile-photo-hint">JPEG, PNG или WebP · до 5 МБ</small>
            {photoError && <p className="profile-message error" role="alert">{photoError}</p>}
          </div>
        </aside>

        <div className="profile-settings">
          <form className="profile-section" onSubmit={saveName}>
            <div className="profile-section-heading"><span><UserRound /></span><div><h3>Личные данные</h3><p>Имя видно коллегам в заявках, чатах и отчётах.</p></div></div>
            <div className="profile-field-grid">
              <label><span>Имя пользователя</span><input name="fullName" value={fullName} onChange={event => setFullName(event.target.value)} minLength={2} maxLength={160} required /></label>
              <label><span>Email</span><input name="email" value={user.email} readOnly /><small>Email используется для входа и меняется администратором.</small></label>
            </div>
            {profileError && <p className="profile-message error" role="alert">{profileError}</p>}
            <div className="profile-section-actions"><button className="profile-button primary" disabled={Boolean(pending) || fullName.trim() === user.fullName}><Save /> {pending === 'name' ? 'Сохраняем…' : 'Сохранить имя'}</button></div>
          </form>

          <section className="profile-section profile-security-section">
            <div className="profile-section-heading"><span><KeyRound /></span><div><h3>Безопасность</h3><p>Используйте уникальный пароль длиной от 12 символов.</p></div></div>
            {passwordAvailable ? <form onSubmit={savePassword}>
              <div className="profile-password-grid">
                <label><span>Текущий пароль</span><input name="currentPassword" type="password" autoComplete="current-password" required /></label>
                <label><span>Новый пароль</span><input name="newPassword" type="password" autoComplete="new-password" minLength={12} required /></label>
                <label><span>Повторите новый пароль</span><input name="passwordConfirmation" type="password" autoComplete="new-password" minLength={12} required /></label>
              </div>
              {passwordError && <p className="profile-message error" role="alert">{passwordError}</p>}
              <div className="profile-section-actions"><button className="profile-button secondary" disabled={Boolean(pending)}><KeyRound /> {pending === 'password' ? 'Изменяем…' : 'Изменить пароль'}</button></div>
            </form> : <div className="profile-directory-note"><ShieldCheck /><span><b>Пароль управляется Active Directory</b><small>Для смены пароля используйте корпоративную службу учётных записей.</small></span></div>}
          </section>
        </div>
      </div>

      <footer className="profile-dialog-footer">
        <div aria-live="polite">{notice && <span><CheckCircle2 /> {notice}</span>}</div>
        <button type="button" className="profile-button secondary" onClick={onClose} disabled={Boolean(pending)}>Готово</button>
      </footer>
    </section>
  </div>;
}
